// apps/roleforge-app/src/components/vertical/AabSoftRedirectModal.tsx
// Plan 3b-ii — Phase 3 (§5.3.1): Soft redirect modal nudging info-business users to AtomicAIBuilder.com.
//
// Issue #1221 — this dialog declared `aria-modal` but never took focus and
// never trapped it, so keyboard and screen-reader users were left on the page
// behind it. `useDialogFocus` supplies the three behaviours `aria-modal`
// implies: focus in, focus trapped, Escape closes and restores focus.

// Issue #1085 — the CTA below used to hardcode `https://atomicaibuilder.com`,
// which now only redirects to the real host. The origin comes from config
// (`aabAppUrl()`), so this live marketing link has no second source of truth.

import { useDialogFocus } from '../../lib/use-dialog-focus';
import { aabAppUrl } from '../../lib/aab-app-url';

interface Props {
  onClose: () => void;
}

export function AabSoftRedirectModal({ onClose }: Props) {
  const dialogRef = useDialogFocus<HTMLDivElement>(onClose);

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="aab-modal-title"
      aria-describedby="aab-modal-body"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
    >
      <div className="relative bg-card border border-line rounded-2xl shadow-soft max-w-md w-full p-8 flex flex-col gap-4">
        <h2 id="aab-modal-title" className="text-lg font-semibold text-ink">
          Looks like you&apos;re building something new
        </h2>
        <p id="aab-modal-body" className="text-sm text-graphite">
          RoleForge is designed for businesses that are already operating. If you&apos;re launching a new
          info product or course business, <strong>AtomicAIBuilder</strong> is the better fit — it&apos;s
          purpose-built for your use case.
        </p>
        <div className="flex flex-col gap-2">
          <a
            href={aabAppUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-h-[44px] items-center justify-center rounded-xl bg-plum text-[#0B0E14] px-4 py-2 text-sm font-semibold hover:opacity-90 transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral"
          >
            Visit AtomicAIBuilder
          </a>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-[44px] items-center justify-center rounded-xl border border-line text-ink px-4 py-2 text-sm font-semibold hover:bg-card-hover transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral"
          >
            Continue with RoleForge
          </button>
        </div>
      </div>
    </div>
  );
}
